import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/shop';
import type { LiveAd } from '../api/types';

/**
 * The super admin's welcome ad, on the website (asked for 2026-11-03; the app has
 * had it since SOW 2.6). An image and/or a video, a headline, a button, and
 * "Don't show this again".
 *
 * HOW OFTEN is decided HERE, in this browser - a shopper who has not signed in has
 * no identity for the server to count against (decided 2026-11-03: once per
 * device, like the app). Per ad, localStorage keeps which version was shown, when,
 * and whether the shopper said never again:
 *   showOnce          shown once; an EDIT counts as a new ad, so a corrected price
 *                     is seen.
 *   every N hours     not again until N hours after it was last shown.
 *   don't show again  never again for this ad, edited or not - that is what the
 *                     shopper asked for.
 * The time is written when the card is SHOWN, not when it is closed: a shopper
 * who leaves the page with it open has still seen it.
 *
 * NEVER IN THE WAY. Only on the pages people arrive at to browse (home, a deal) -
 * never on a checkout, a voucher pass shown at a counter, an unsubscribe link or a
 * print page. Any failure (offline, the API, storage refused in a private window)
 * means no card, silently.
 */

const KEY_PREFIX = 'dd.ad.';

/** Pages where a pop-up would get between somebody and what they came to do. */
function isBrowsePage(path: string): boolean {
  return path === '/' || path.startsWith('/deals/');
}

interface Seen {
  version: string;
  shownAt: number;
  never?: boolean;
}

function read(id: string): Seen | null {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + id);
    return raw === null ? null : (JSON.parse(raw) as Seen);
  } catch {
    return null;
  }
}

function write(id: string, seen: Seen) {
  try {
    window.localStorage.setItem(KEY_PREFIX + id, JSON.stringify(seen));
  } catch {
    // Storage refused (a private window, full storage). Seeing it again is the cost.
  }
}

/** Whether this browser should see the ad now. Exported for its tests. */
export function isDue(ad: LiveAd, seen: Seen | null, now: number): boolean {
  if (seen === null) return true;
  if (seen.never === true) return false;
  if (seen.version !== ad.version) return true;
  if (ad.showOnce === true) return false;
  return now - seen.shownAt >= ad.frequencyHours * 60 * 60 * 1000;
}

export function WelcomeAd() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [ad, setAd] = useState<LiveAd | null>(null);
  const [never, setNever] = useState(false);
  const checked = useRef(false);

  useEffect(() => {
    // Once per page load, and only if it started on a page made for browsing.
    if (checked.current || !isBrowsePage(pathname)) return;
    checked.current = true;

    let cancelled = false;

    api.appOpenAd()
      .then((live) => {
        if (cancelled || live === undefined || live === null) return;
        if (!isDue(live, read(live.id), Date.now())) return;

        write(live.id, { version: live.version, shownAt: Date.now() });
        setAd(live);
      })
      .catch(() => { /* no card is the right outcome */ });

    return () => { cancelled = true; };
  }, [pathname]);

  useEffect(() => {
    if (ad === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (ad === null) return null;

  function close() {
    if (ad !== null && never) {
      write(ad.id, { version: ad.version, shownAt: Date.now(), never: true });
    }
    setAd(null);
  }

  function act() {
    const current = ad;
    close();
    if (current === null) return;

    if (current.dealId !== null) {
      navigate(`/deals/${current.dealId}`);
    } else if (current.linkUrl !== null) {
      // https only - the server refuses anything else when the ad is saved.
      window.open(current.linkUrl, '_blank', 'noopener');
    }
  }

  const hasAction = ad.dealId !== null || ad.linkUrl !== null;

  return (
    <div
      className="dialog-backdrop welcome-ad-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="dialog welcome-ad" role="dialog" aria-modal="true" aria-labelledby="welcome-ad-title">
        <button type="button" className="welcome-ad-close" aria-label="Close" onClick={close}>×</button>

        {ad.videoUrl ? (
          // Muted, so the browser lets it play by itself; the controls unmute it.
          <video
            className="welcome-ad-media"
            src={ad.videoUrl}
            poster={ad.imageUrl ?? undefined}
            autoPlay
            muted
            playsInline
            controls
            preload="metadata"
          />
        ) : ad.imageUrl ? (
          <img className="welcome-ad-media" src={ad.imageUrl} alt="" />
        ) : null}

        <h2 id="welcome-ad-title">{ad.title}</h2>
        {ad.body && <p className="dialog-body">{ad.body}</p>}

        <label className="welcome-ad-never">
          <input type="checkbox" checked={never} onChange={(e) => setNever(e.target.checked)} />
          <span>Don't show this again</span>
        </label>

        <div className="dialog-actions">
          <button type="button" className="btn secondary" onClick={close}>Close</button>
          {hasAction && (
            <button type="button" className="btn" onClick={act}>{ad.ctaLabel || 'Take a look'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
